import { Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { WhatsAppActions } from "@/components/WhatsAppActions";
import { useJournalArticles } from "@/hooks/useJournalArticles";
import type { ArticleItem } from "@/lib/journal-defaults";

const PLACEHOLDERS = [
  { tag: "Nutrition", title: "Journal post coming soon" },
  { tag: "Kitchen", title: "Journal post coming soon" },
  { tag: "Sourcing", title: "Journal post coming soon" },
];

function latestThree(articles: ArticleItem[]): ArticleItem[] {
  return [...articles]
    .sort((a, b) => {
      const at = a.publishedAt ? Date.parse(a.publishedAt) : 0;
      const bt = b.publishedAt ? Date.parse(b.publishedAt) : 0;
      if (bt !== at) return bt - at;
      return b.id.localeCompare(a.id);
    })
    .slice(0, 3);
}

export function JournalPreview() {
  const { articles, isFallback } = useJournalArticles();
  const posts = isFallback ? [] : latestThree(articles);

  return (
    <Section>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Journal</span>
          <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">Recipes, notes &amp; nutrition.</h2>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-4">
          <Link to="/blog" className="text-sm font-medium text-primary hover:underline">Visit journal →</Link>
          <WhatsAppActions location="journal-preview-cta" compact chatLabel="Chat on WhatsApp" />
        </div>
      </div>
      {posts.length > 0 ? (
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {posts.map((post) => (
            <Link
              key={post.id}
              to="/blog/$slug"
              params={{ slug: post.slug }}
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-shadow duration-300 hover:shadow-sm"
            >
              <div className="relative">
                <img
                  src={post.image}
                  alt={post.alt}
                  loading="lazy"
                  className="aspect-[16/10] w-full object-cover"
                />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  <span>{post.category}</span>
                </div>
                <h3 className="mt-3 font-display text-xl font-medium leading-snug group-hover:text-primary">
                  {post.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{post.excerpt}</p>
                <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {post.author ? <span>{post.author}</span> : null}
                  {post.author && post.date ? <span aria-hidden>·</span> : null}
                  {post.date ? <span>{post.date}</span> : null}
                  {post.date && post.readTime ? <span aria-hidden>·</span> : null}
                  {post.readTime ? <span>{post.readTime}</span> : null}
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {PLACEHOLDERS.map((p, i) => (
            <article key={i} className="flex h-full flex-col rounded-2xl border border-dashed border-border bg-card p-6">
              <div className="aspect-[16/10] w-full rounded-lg bg-secondary" aria-hidden />
              <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                <span>{p.tag}</span>
                <span className="rounded-full bg-secondary px-2 py-0.5 text-foreground">Coming Soon</span>
              </div>
              <h3 className="mt-3 font-display text-xl font-medium">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                We're preparing our first articles. Check back soon.
              </p>
            </article>
          ))}
        </div>
      )}
    </Section>
  );
}
